/**
 * Mobile Authentication Bridge Service
 * 
 * This service handles communication between the web application and the Flutter
 * mobile app for authentication flows. It provides methods to trigger native
 * authentication modals and handle authentication tokens from the mobile app.
 */

import { isMobileWebView, isMobileAuthBridgeAvailable, logEnvironmentInfo } from './mobileDetection';
import { toast } from '@/hooks/use-toast';

export type AuthTab = 'signIn' | 'signUp';

/**
 * Interface for mobile authentication events
 */
interface MobileAuthCallbacks {
  onAuthSuccess?: (token: string) => void;
  onAuthError?: (error: string) => void;
}

/**
 * Mobile Authentication Bridge Class
 */
class MobileAuthBridgeService {
  private static instance: MobileAuthBridgeService;
  private callbacks: MobileAuthCallbacks = {};
  private isInitialized = false;

  private constructor() {
    this.initialize();
  }

  public static getInstance(): MobileAuthBridgeService {
    if (!MobileAuthBridgeService.instance) {
      MobileAuthBridgeService.instance = new MobileAuthBridgeService();
    }
    return MobileAuthBridgeService.instance;
  }

  /**
   * Initialize the mobile auth bridge
   */
  private initialize(): void {
    if (this.isInitialized) return;

    // Log environment information for debugging
    logEnvironmentInfo();

    // Set up global handler for receiving auth tokens from Flutter
    this.setupTokenReceiver();

    this.isInitialized = true;
  }

  /**
   * Setup the global function to receive authentication tokens from Flutter
   */
  private setupTokenReceiver(): void {
    // Create global handler for native auth success
    (window as any).onNativeAuthSuccess = (token: string) => {
      if (this.callbacks.onAuthSuccess) {
        this.callbacks.onAuthSuccess(token);
      }
    };

    // Create global handler for native auth errors
    (window as any).onNativeAuthError = (error: string) => {
      if (this.callbacks.onAuthError) {
        this.callbacks.onAuthError(error);
      } else {
        // Show default error toast
        toast({
          title: "Authentication Error",
          description: error || "An error occurred during authentication",
          variant: "destructive",
        });
      }
    };
  }

  /**
   * Register callbacks for authentication events
   */
  public setCallbacks(callbacks: MobileAuthCallbacks): void {
    this.callbacks = { ...this.callbacks, ...callbacks };
  }

  /**
   * Clear registered callbacks
   */
  public clearCallbacks(): void {
    this.callbacks = {};
  }

  /**
   * Trigger native authentication modal in the Flutter app
   * @param tab - The authentication tab to show ('signIn' or 'signUp')
   * @returns Promise that resolves when the message is sent (not when auth completes)
   */
  public async triggerNativeAuth(tab: AuthTab = 'signIn'): Promise<void> {
    if (!isMobileWebView()) {
      throw new Error('Not in mobile WebView environment');
    }

    if (!isMobileAuthBridgeAvailable()) {
      throw new Error('Mobile auth bridge is not available');
    }

    try {
      const message = `showAuth:${tab}`;
      // Send message to Flutter via the AuthChannel
      (window as any).AuthChannel.postMessage(message);
    } catch (error) {
      throw new Error(`Failed to trigger native authentication: ${error}`);
    }
  }

  /**
   * Check if native authentication should be used instead of web modals
   * @returns True if we should use native authentication
   */
  public shouldUseNativeAuth(): boolean {
    return isMobileWebView() && isMobileAuthBridgeAvailable();
  }

  /**
   * Handle authentication token received from the mobile app
   * This method can be called manually if needed, but is typically handled automatically
   * @param token - The Firebase ID token from the mobile app
   */
  public handleAuthToken(token: string): void {
    try {
      // Store the token in a cookie for the web app to use
      document.cookie = `firebase-token=${token}; path=/; secure; samesite=lax`;
      
      // Also store in sessionStorage as a backup
      sessionStorage.setItem('firebase-token', token);
      
      // Trigger any registered success callback
      if (this.callbacks.onAuthSuccess) {
        this.callbacks.onAuthSuccess(token);
      }
      
    } catch (error) {
      
      if (this.callbacks.onAuthError) {
        this.callbacks.onAuthError(`Token processing failed: ${error}`);
      }
    }
  }

  /**
   * Get environment information for debugging
   */
  public getEnvironmentInfo(): object {
    return {
      isMobileWebView: isMobileWebView(),
      isMobileAuthBridgeAvailable: isMobileAuthBridgeAvailable(),
      userAgent: navigator.userAgent,
      authChannelAvailable: typeof (window as any).AuthChannel !== 'undefined',
      timestamp: new Date().toISOString()
    };
  }
}

// Export the singleton instance
export const mobileAuthBridge = MobileAuthBridgeService.getInstance();

// Export convenience functions
export const shouldUseNativeAuth = () => mobileAuthBridge.shouldUseNativeAuth();
export const triggerNativeAuth = (tab: AuthTab = 'signIn') => mobileAuthBridge.triggerNativeAuth(tab);
export const setMobileAuthCallbacks = (callbacks: MobileAuthCallbacks) => mobileAuthBridge.setCallbacks(callbacks);
export const clearMobileAuthCallbacks = () => mobileAuthBridge.clearCallbacks();